import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    MobileStepper,
    Stack,
    Typography
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import * as endPoints from '@/endpoints'
import ThemeLivePreview from '@/features/account/first-run/ThemeLivePreview'
import ModalProfilePicture from '@/features/dashboard/account/ModalProfilePicture'
import useAuth from '@/hooks/useAuth'
import { useFullScreenDialog } from '@/hooks/useFullScreenDialog'
import axiosInstance from '@/lib/axios'
import { accentColorOptions } from '@/theme/colors'

const STEPS = ['Welcome', 'Profile Picture', 'Appearance']

const themes = [
    { label: 'White', value: 'white' },
    { label: 'Pastel', value: 'pastel' },
    { label: 'Grey', value: 'grey' },
    { label: 'Dark', value: 'dark' },
    { label: 'Black', value: 'black' },
    { label: 'Black X', value: 'black-experimental' }
]

const getUserInitials = (user) =>
    `${user?.firstName?.charAt(0) || ''}${user?.lastName?.charAt(0) || ''}`.toUpperCase() ||
    user?.username?.charAt(0)?.toUpperCase() ||
    '?'

// Step 0: Welcome
const WelcomeStep = () => (
    <Box sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="h4" gutterBottom>
            Welcome to Power
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
            Let&apos;s get you set up in a few quick steps. You can add a
            profile picture and pick a look that suits you.
        </Typography>
    </Box>
)

const uploadAvatar = async (data) => {
    const response = await axiosInstance.patch(
        endPoints.ACCOUNT_DETAILS,
        data,
        {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }
    )

    return response.data
}

const ProfilePhotoStep = ({
    avatarUploading,
    onOpenProfilePhoto,
    onSkip,
    user
}) => (
    <Box>
        <Stack
            spacing={2.5}
            alignItems="center"
            sx={{ textAlign: 'center', py: 1 }}
        >
            <Box
                sx={(theme) => ({
                    p: 0.75,
                    border: `1px dashed ${theme.palette.divider}`,
                    borderRadius: '50%'
                })}
            >
                {avatarUploading ? (
                    <Box
                        sx={{
                            width: 144,
                            height: 144,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <Avatar
                        src={user?.avatar_image || user?.avatar || undefined}
                        sx={{
                            width: 144,
                            height: 144,
                            fontSize: '3rem',
                            bgcolor: 'primary.main'
                        }}
                    >
                        {getUserInitials(user)}
                    </Avatar>
                )}
            </Box>

            <Box>
                <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="center"
                    sx={{ mb: 1 }}
                >
                    <Chip size="small" label="Optional" />
                    {user?.avatar_image && (
                        <Chip size="small" color="success" label="Added" />
                    )}
                </Stack>
                <Typography variant="h6" gutterBottom>
                    Add a profile picture
                </Typography>
                <Typography
                    color="text.secondary"
                    variant="body2"
                    sx={{ maxWidth: 480 }}
                >
                    A profile picture helps other people recognise you in
                    groups, events, and messages. You can skip this for now and
                    add one later in your account settings.
                </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                    variant="contained"
                    onClick={onOpenProfilePhoto}
                    disabled={avatarUploading}
                >
                    {user?.avatar_image
                        ? 'Change profile picture'
                        : 'Set profile picture'}
                </Button>
                <Button
                    variant="text"
                    color="inherit"
                    onClick={onSkip}
                    disabled={avatarUploading}
                >
                    Skip for now
                </Button>
            </Stack>
        </Stack>
    </Box>
)

ProfilePhotoStep.propTypes = {
    avatarUploading: PropTypes.bool,
    onOpenProfilePhoto: PropTypes.func.isRequired,
    onSkip: PropTypes.func.isRequired,
    user: PropTypes.object
}

// Appearance step
const AppearanceStep = ({
    themeValue,
    colorPreset,
    onThemeChange,
    onColorChange,
    roundedCorners,
    styleConfig
}) => (
    <Box>
        <Typography variant="h6" gutterBottom>
            Choose your look
        </Typography>
        <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
            Pick a theme and accent colour. You can always change these later in
            Display Settings.
        </Typography>

        <Typography color="text.secondary" variant="overline">
            Theme
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 3, mt: 1 }}>
            {themes.map(({ label, value }) => (
                <Button
                    key={value}
                    onClick={() => onThemeChange(value)}
                    size="small"
                    variant={themeValue === value ? 'contained' : 'outlined'}
                    color={themeValue === value ? 'primary' : 'inherit'}
                    sx={{
                        borderRadius: roundedCorners ? 999 : 0,
                        textTransform: 'none'
                    }}
                >
                    {label}
                </Button>
            ))}
        </Stack>

        <Typography color="text.secondary" variant="overline">
            Accent Colour
        </Typography>
        <Stack
            alignItems="center"
            direction="row"
            flexWrap="wrap"
            gap={1}
            sx={{ mt: 1 }}
        >
            {accentColorOptions.map((option) => (
                <Chip
                    label={option.label}
                    icon={
                        <Box
                            sx={{
                                backgroundColor: option.color,
                                flexShrink: 0,
                                height: 24,
                                width: 36
                            }}
                        />
                    }
                    key={option.value}
                    onClick={() => onColorChange(option.value)}
                    sx={[
                        {
                            borderColor: 'transparent',
                            borderStyle: 'solid',
                            borderWidth: 2,
                            height: '34px',
                            p: '4px',
                            '& .MuiChip-icon': { m: 0 },
                            '& .MuiChip-label': { px: 1.25 }
                        },
                        option.value === colorPreset && {
                            borderColor: 'primary.main'
                        }
                    ]}
                />
            ))}
        </Stack>

        <Typography color="text.secondary" variant="overline" sx={{ mt: 3 }}>
            Preview
        </Typography>
        <Box sx={{ mt: 1 }}>
            <ThemeLivePreview
                themeValue={themeValue}
                colorPreset={colorPreset}
                styleConfig={styleConfig}
            />
        </Box>
    </Box>
)

AppearanceStep.propTypes = {
    themeValue: PropTypes.string,
    colorPreset: PropTypes.string,
    onThemeChange: PropTypes.func.isRequired,
    onColorChange: PropTypes.func.isRequired,
    roundedCorners: PropTypes.bool,
    styleConfig: PropTypes.shape({
        containerStretch: PropTypes.bool,
        contrast: PropTypes.string,
        direction: PropTypes.string,
        layout: PropTypes.string,
        navColor: PropTypes.string,
        responsiveFontSizes: PropTypes.bool,
        roundedCorners: PropTypes.bool
    }).isRequired
}

// Main dialog
const OnboardingDialog = () => {
    const fullScreen = useFullScreenDialog()
    const {
        isAuthenticated,
        refreshSession,
        settings,
        updateSettings,
        updateUser,
        user
    } = useAuth()
    const [step, setStep] = useState(0)
    const [profilePicModalOpen, setProfilePicModalOpen] = useState(false)
    const [saving, setSaving] = useState(false)

    const [themeValue, setThemeValue] = useState(settings.style.theme)
    const [colorPreset, setColorPreset] = useState(settings.style.colorPreset)

    const onboardingCompleted = settings?.onboarding?.completed === true
    const open = isAuthenticated && !onboardingCompleted

    useEffect(() => {
        setThemeValue(settings.style.theme)
        setColorPreset(settings.style.colorPreset)
    }, [settings.style.colorPreset, settings.style.theme])

    const avatarMutation = useMutation({
        mutationFn: ({ ...values }) => uploadAvatar(values),
        onSuccess: (data) => {
            updateUser({ ...data })
            toast.success('Profile picture updated')
        },
        onError: (error) => {
            console.error(error)
            toast.error(
                'Something went wrong while uploading your profile picture.'
            )
        }
    })

    const handleThemeChange = (value) => {
        setThemeValue(value)
        updateSettings({
            model: 'user',
            attribute: ['style'],
            value: { ...settings.style, theme: value, colorPreset }
        })
    }

    const handleColorChange = (value) => {
        setColorPreset(value)
        updateSettings({
            model: 'user',
            attribute: ['style'],
            value: { ...settings.style, theme: themeValue, colorPreset: value }
        })
    }

    const handleComplete = async () => {
        setSaving(true)
        try {
            await refreshSession()
            updateSettings({
                model: 'user',
                attribute: ['onboarding'],
                value: { completed: true }
            })
        } finally {
            setSaving(false)
        }
    }

    const handleNext = () => {
        if (step < STEPS.length - 1) {
            setStep((s) => s + 1)
        } else {
            handleComplete()
        }
    }

    const handleBack = () => setStep((s) => s - 1)

    if (!open) return null

    return (
        <Dialog
            fullScreen={fullScreen}
            open={open}
            maxWidth="md"
            fullWidth
            disableEscapeKeyDown
            slotProps={{ backdrop: { sx: { backdropFilter: 'blur(4px)' } } }}
        >
            <DialogContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="overline" color="text.secondary">
                        Step {step + 1} of {STEPS.length}: {STEPS[step]}
                    </Typography>
                </Box>

                {step === 0 && <WelcomeStep />}
                {step === 1 && (
                    <ProfilePhotoStep
                        avatarUploading={avatarMutation.isPending}
                        onOpenProfilePhoto={() => setProfilePicModalOpen(true)}
                        onSkip={handleNext}
                        user={user}
                    />
                )}
                {step === 2 && (
                    <AppearanceStep
                        themeValue={themeValue}
                        colorPreset={colorPreset}
                        onThemeChange={handleThemeChange}
                        onColorChange={handleColorChange}
                        roundedCorners={settings.style.roundedCorners}
                        styleConfig={settings.style}
                    />
                )}

                <ModalProfilePicture
                    openState={profilePicModalOpen}
                    closeModal={() => setProfilePicModalOpen(false)}
                    mutation={avatarMutation}
                />
            </DialogContent>
            {/* Outside DialogContent so Back/Next stay pinned while a step
                scrolls — the dialog is full screen on a phone. */}
            <MobileStepper
                variant="dots"
                steps={STEPS.length}
                position="static"
                activeStep={step}
                sx={{
                    background: 'transparent',
                    px: { xs: 2.5, sm: 4 },
                    pb: { xs: 2.5, sm: 4 },
                    pt: 0
                }}
                nextButton={
                    <Button
                        size="small"
                        onClick={handleNext}
                        disabled={saving}
                        variant="contained"
                    >
                        {saving ? (
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                        ) : null}
                        {step === STEPS.length - 1 ? 'Get started' : 'Next'}
                    </Button>
                }
                backButton={
                    <Button
                        size="small"
                        onClick={handleBack}
                        disabled={step === 0}
                    >
                        Back
                    </Button>
                }
            />
        </Dialog>
    )
}

export default OnboardingDialog
